package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/mvi")
@Composable
fun CleanArchitectureMMVIPage() {
    BlogLayout(
        PageProperties(
            "MVI Architecture",
            "MVI (Model-View-Intent) is a unidirectional data flow pattern. The View sends Intents (user actions), the Model processes them and produces new State, and the View renders the State — making state predictable and testable."
        ),
        BlogProperties("Jetpack Compose / Kotlin Multiplatform", "MVI")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            KotlinCode(
                code = """
                    //Instead of writing:
                    response.error ?: "request_failed"
                    
                    //You can make it more robust:
                    response.error?.orEmpty().ifBlank { "request_failed" }
                    """.trimIndent()
            )

            // The UI sends intents or events, the state is immutable, and the UI renders from a single state object. This makes state changes easier to trace.
            SpanText(
                text = "MVI - (Intent → Reducer → State → UI)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It takes the idea of “state” and makes it incredibly strict through Unidirectional Data Flow (UDF) and immutable state management. Intent → Model → State → View. The ViewModel holds a single source of truth as a StateFlow of UiState, and emits SharedFlow events (UiEffect) for one-time actions like navigation and Snackbar messages.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In MVI, data flows from Intent (user actions) to the ViewModel, which processes the actions through business logic (often represented by Use Cases) and updates the state. The state is then emitted to the View, creating a reactive interface where each UI change corresponds to a specific state update. Immutable state means that the ViewModel only emits updated copies of the state, enhancing predictability and reducing the risk of unintended side effects. This single source of truth approach ensures that all changes in the UI are fully controlled, making state management clear, testable, and reliable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVI enforces three simple but powerful rules:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ol {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Single Source of Truth (State)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Single Entry Point (Intent)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Single Exit Point (Effect)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVI’s core principle is simplicity and predictability, achieved through a strict circular data flow:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Intent - (Action/Event): we define the Intent, which represents the user’s intention to perform an action that affects the state of the UI. A sealed interface representing user actions (e.g., OnSubmitClicked). The View sends an Intent (a user action or lifecycle event) to the ViewModel. Describes what happened Processed by ViewModel. Triggers state transitions",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Model (State): The ViewModel processes. Immutable data class. Single source of truth. Represents the entire UI state. Emitted as a StateFlow",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )

                        SpanText(
                            text = "State is an immutable snapshot of everything the screen needs in order to render. If it is not in the state, it cannot appear on screen.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )

                        Ol {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "sealed class - Sealed class when every state shares common data or behavior.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "sealed interface",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .textAlign(TextAlign.Start)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "internal sealed interface",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .textAlign(TextAlign.Start)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "object",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .textAlign(TextAlign.Start)
                                )
                            }
                        }
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "View: The View simply observes and renders the current State.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State (The Single UI Model): This replaces fragmented data (e.g., separate isLoading, errorMessage, and profileData variables) with a single, comprehensive object. Immutable Kotlin data class holding the exact UI snapshot.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Side Effects (One-time Events): These handle non-state-changing events, like navigation, toasts, or dialogs. This is an MVI improvement over MVVM where these were often handled via SingleLiveEvent.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }

            SpanText(
                text = "Comparing Architectural Patterns",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVC - Bidirectional, Controller-heavy",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVP - View ↔ Presenter, 1:1 interfaces",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVVM - View observes ViewModel state",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVI - View → Intent → Reducer → immutable State → View",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    ┌───────────┐ Intent      ┌──────────────┐
                    │ View      │───────────▶ │ ViewModel    │
                    │ (Compose) │             │ (MVI core)   │
                    └─────▲─────┘             └──────┬───────┘
                          │                          │ reduce(state, intent) -> newState
                          │         StateFlow<State> ▼
                          └──────────────────────────┘
                                                │ SideEffect (Channel/SharedFlow)
                                                ▼
                                            one-off events (navigate, toast)
                    """.trimIndent()
            )

            SpanText(
                text = "MVI with Reducer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Reducer pattern introduces a pure function that takes the current State + Intent and returns a new State. This keeps the ViewModel clean and makes state transitions highly testable.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Model
                    data class CounterState(val count: Int = 0)
                    
                    //Intent
                    sealed class CounterIntent {
                        object Increment : CounterIntent()
                        object Decrement : CounterIntent()
                        data class SetCount(val count: Int) : CounterIntent()
                    }
                    
                    //Reducer
                    class CounterReducer : Reducer<CounterState, CounterIntent> {
                        override fun reduce(state: CounterState, intent: CounterIntent): CounterState {
                            return when (intent) {
                                is CounterIntent.Increment -> {
                                    state.copy(count = state.count + 1)
                                }
                                is CounterIntent.Decrement -> {
                                    state.copy(count = state.count - 1)
                                }
                                is CounterIntent.SetCount -> {
                                    state.copy(count = intent.count)
                                }
                            }
                        }
                    }
                    
                    class CounterViewModel(private val api: CounterApi) : ViewModel() {
                        
                        private val _state = MutableStateFlow(CounterState())
                        val state: StateFlow<CounterState> = _state

                        fun handleIntent(intent: CounterIntent) {
                            viewModelScope.launch {
                                when (intent) {
                                    is CounterIntent.Increment -> {
                                        try {
                                            val result = withContext(Dispatchers.IO) { api.incrementCounter() }
                                            val newState = counterReducer(_state.value, CounterIntent.SetCount(result.count))
                                            _state.emit(newState)
                                        } catch (e: Exception) {
                                            // Handle error
                                        }
                                    }
                                    is CounterIntent.Decrement -> {
                                        try {
                                            val result = withContext(Dispatchers.IO) { api.decrementCounter() }
                                            val newState = counterReducer(_state.value, CounterIntent.SetCount(result.count))
                                            _state.emit(newState)
                                        } catch (e: Exception) {
                                            // Handle error
                                        }
                                    }
                                    is CounterIntent.SetCount -> {
                                        val newState = counterReducer(_state.value, intent)
                                        _state.emit(newState)
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "MVI with State Machine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Finite State Machine (FSM) models your UI as a set of valid states and allowed transitions between them.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                 Ul {
                     Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                         SpanText(
                             text = "You’re always in one valid state.",
                             modifier = Modifier.fontSize(FontSize.Medium)
                         )
                     }

                     Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                         SpanText(
                             text = "You can only move via defined transitions.",
                             modifier = Modifier.fontSize(FontSize.Medium)
                         )
                     }

                     Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                         SpanText(
                             text = "Impossible transitions are ignored.",
                             modifier = Modifier.fontSize(FontSize.Medium)
                         )
                     }
                 }
            }

            SpanText(
                text = "When to use State Machine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multi-step workflows (checkout, onboarding, authentication).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When invalid states are dangerous (banking, healthcare).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When debugging is easier if you see exact state transitions.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }
            KotlinCode(
                code = """
                    // States
                    sealed class CheckoutState {
                        object Idle : CheckoutState()
                        object CollectingAddress : CheckoutState()
                        object CollectingPayment : CheckoutState()
                        object ProcessingPayment : CheckoutState()
                        object Success : CheckoutState()
                        data class Error(val message: String) : CheckoutState()
                    }

                    // Intents
                    sealed class CheckoutIntent {
                        object StartCheckout : CheckoutIntent()
                        data class EnterAddress(val address: String) : CheckoutIntent()
                        data class EnterPayment(val card: String) : CheckoutIntent()
                        object ConfirmPayment : CheckoutIntent()
                        object Retry : CheckoutIntent()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "What is the difference between designing a ViewModel’s public state as a single sealed-class “screen state” (sealed class ScreenState { Loading; Content; Error}) versus multiple independent StateFlows for different concerns (loading boolean, data, error message)?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A single sealed-class state guarantees mutual exclusivity and exhaustive handling of screen states at the type level (you can’t accidentally represent “loading AND showing stale error” simultaneously if the sealed hierarchy doesn’t allow it). Multiple independent StateFlows are more flexible for genuinely independent concerns that can coexist (e.g., a persistent header state independent of a paginated list’s loading state), but risk representing invalid/inconsistent combined states if the independent flows aren’t carefully coordinated",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    sealed interface ProfileState {

                        data object Loading : ProfileState

                        data object Error : ProfileState

                        data class Success(
                            val firstName: String,
                            val lastName: String,
                            val photos: List<Photo>,
                            val isUpgradeVisible: Boolean,
                            val notificationsCount: Int
                        ) : ProfileState
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // ✅ MVI: One sealed class — only ONE state can be active at a time. 
                    sealed class LoginState {
                        object Idle : LoginState()
                        object Loading : LoginState()
                       
                        data class Success(val user: User) : LoginState()
                        data class Error(val message: String) : LoginState()
                        data class ValidationError(
                            val emailError: String? = null,
                            val passwordError: String? = null
                        ) : LoginState()
                        // Loading AND Success simultaneously? Structurally impossible.
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    internal sealed interface MoviesListUiState {
                        data object Loading : MoviesListUiState
                        
                        data class Success(val movies: ImmutableList<MovieSection>) : MoviesListUiState
                        data class Error(val message: String) : MoviesListUiState
                    }
                    """.trimIndent()
            )

            //https://github.com/NsAveek/InventoryManagement.git
            KotlinCode(
                code = """
                    // ✅ MVI: Using an object groups everything related to the Dashboard screen into a single namespace.
                    
                    //1
                    object DashboardContract {

                        data class State(
                            val user: User? = null,
                            val cards: List<DashboardCard> = emptyList(),
                            val isLoading: Boolean = false,
                        ) : UiState

                        sealed interface Intent : UiIntent {
                            data object LoadDashboard : Intent
                            data class CardClicked(val card: DashboardCard) : Intent
                            data object LogoutClicked : Intent
                        }

                        sealed interface Effect : UiEffect {
                            data class NavigateTo(val route: String) : Effect
                            data object NavigateToLogin : Effect
                        }
                    }
                    
                    //UI Access
                    @Composable
                    private fun AdminOrderCard(
                        order: Order,
                        onIntent: (DashboardContract.Intent) -> Unit
                    ) { }
                    
                    //2
                    object SignUpContract {

                        data class State(
                            val name: String = "",
                            val email: String = "",
                            val password: String = "",
                            val confirmPassword: String = "",
                            val isLoading: Boolean = false,
                            val errorMessage: String? = null,
                        ) : UiState

                        sealed interface Intent : UiIntent {
                            data class UpdateName(val name: String) : Intent
                            data class UpdateEmail(val email: String) : Intent
                            data class UpdatePassword(val password: String) : Intent
                            data class UpdateConfirmPassword(val confirmPassword: String) : Intent
                            data object CreateAccountClicked : Intent
                            data object SignInClicked : Intent
                            data object BackClicked : Intent
                        }

                        sealed interface Effect : UiEffect {
                            data object NavigateToLogin : Effect
                            data object NavigateBack : Effect
                            data object NavigateToHome : Effect
                            data class ShowError(val message: String) : Effect
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "MVI Structure",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The UI sends intents or actions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ViewModel processes them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State is reduced from the previous state plus the new result.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The UI renders the new state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Side effects are handled explicitly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }


            SpanText(
                text = "MVI Counter App",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed interface CounterAction {
                        data object IncrementClicked: CounterAction
                        data object DecrementClicked: CounterAction
                    }
                    
                    sealed interface CounterIntent{
                        data object CounterIncrement: CounterIntent
                        data object CounterDecrement: CounterIntent
                    }
                    
                    interface CounterResult {
                        data class UpdatedCounter(val counter:Int): CounterResult
                    }
                    
                    class CounterModel {
                        private var counter: Int = 0
                        fun increment() {
                            counter++
                        }

                        fun decrement() {
                            counter--
                        }

                        fun getCounter() = counter
                    }
                    
                    @Immutable
                    data class CounterUiState(
                        val counter: Int = 0
                    )
                    
                    class CounterViewModel: ViewModel() {
                        private val counterModel = CounterModel()
                        private val _uiState = MutableStateFlow<CounterUiState>(CounterUiState())
                        val uiState= _uiState.asStateFlow()

                    //intent -> action
                        fun onIntent(intent: CounterIntent){
                            when(intent){
                                is CounterIntent.CounterIncrement ->handleAction(CounterAction.IncrementClicked)
                                is CounterIntent.CounterDecrement -> handleAction(CounterAction.DecrementClicked)
                            }
                        }
                        //action ->Result

                        fun handleAction(action: CounterAction) {
                            when (action) {
                                is CounterAction.IncrementClicked -> {
                                    counterModel.increment()
                                   result(CounterResult.UpdatedCounter(counterModel.getCounter()))

                                }
                                is CounterAction.DecrementClicked -> {
                                   counterModel.decrement()
                                    result(CounterResult.UpdatedCounter(counterModel.getCounter()))
                                }
                            }
                        }

                        //result->update ui state
                        fun result(result: CounterResult){
                            when(result) {
                               is CounterResult.UpdatedCounter-> {
                                    _uiState.update { it.copy(counter = result.counter) }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //1. MutableStateFlow using
                    
                    data class ArticlesUiState(
                        val isLoading: Boolean = false,
                        val items: List<Article> = emptyList(),
                        val error: String? = null
                    )

                    sealed interface ArticlesIntent {
                        data object Load : ArticlesIntent
                        data object Retry : ArticlesIntent
                        data class FilterChanged(val query: String) : ArticlesIntent
                    }

                    class ArticlesViewModel(
                        private val repository: ArticlesRepository
                    ) : ViewModel() {
                        private val _state = MutableStateFlow(ArticlesUiState())
                        val state: StateFlow<ArticlesUiState> = _state
                       
                        fun onIntent(intent: ArticlesIntent) {
                            when (intent) {
                                ArticlesIntent.Load,
                                ArticlesIntent.Retry -> loadArticles()
                                is ArticlesIntent.FilterChanged -> {
                                    _state.update {
                                        it.copy(
                                            items = it.items.filter { article ->
                                                article.title.contains(intent.query, ignoreCase = true)
                                            }
                                        )
                                    }
                                }
                            }
                        }
                        
                        private fun loadArticles() {
                            viewModelScope.launch {
                                _state.update { it.copy(isLoading = true, error = null) }
                                runCatching { repository.getArticles() }
                                    .onSuccess { items ->
                                        _state.update { it.copy(isLoading = false, items = items) }
                                    }
                                    .onFailure { error ->
                                        _state.update {
                                            it.copy(
                                                isLoading = false,
                                                error = error.message ?: "Something went wrong"
                                            )
                                        }
                                    }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //field using 
                    
                    @Immutable
                    internal sealed interface TopSellingIntent {
                        data object Load : TopSellingIntent
                        data object Retry : TopSellingIntent
                    }
                    
                    @Stable
                    internal data class TopSellingUiState(
                        val isLoading: Boolean = false,
                        val salesList: ImmutableList<TopSelling> = persistentListOf(),
                        val error: Throwable? = null
                    )
                    
                    internal class TopSellingViewModel(private val topSellingUseCase: TopSellingUseCase) : ViewModel() {

                        val topSellingFlow: StateFlow<TopSellingUiState> field = MutableStateFlow(TopSellingUiState())

                        fun onIntent(intent: TopSellingIntent) {
                            when (intent) {
                                TopSellingIntent.Load -> topSellingList()
                                TopSellingIntent.Retry -> topSellingList()
                            }
                        }

                        private fun topSellingList() {
                            viewModelScope.launch {
                                topSellingFlow.update { it.copy(isLoading = true) }

                                topSellingUseCase().fold(
                                    ifLeft = { error ->  topSellingFlow.update { it.copy(isLoading = false, error = error) } },
                                    ifRight = { response -> topSellingFlow.update { it.copy(isLoading = false, salesList = response.toImmutableList()) } }
                                )
                            }
                        }

                        init {
                            onIntent(TopSellingIntent.Load)
                        }
                    }
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    internal fun TopSellingScreen(
                        viewModel: TopSellingViewModel = koinViewModel(),
                    ) {
                        val state by viewModel.topSellingFlow.collectAsStateWithLifecycle()

                        TopSellingUiState(state, onIntent = viewModel::onIntent)
                    }

                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    private fun TopSellingUiState(
                        state: TopSellingUiState,
                        onIntent: (TopSellingIntent) -> Unit
                    ) = with(state) {
                        when {
                            //Loading Handle
                            isLoading -> { }

                            //Error Handle    
                            error != null -> { }

                            //Success handle     
                            salesList.isNotEmpty() -> { }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "MVI + Clean Architecture (Combined)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Presentation: Intent, State, ViewModel",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Domain: UseCases",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data: Repositories, API, DB",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    kotlin = "2.3.21"
                    arrow = "2.2.2.1"
                    
                    arrow-core = { group = "io.arrow-kt", name = "arrow-core", version.ref = "arrow" }
                    arrow-coroutines = { group = "io.arrow-kt", name = "arrow-fx-coroutines", version.ref = "arrow" }
                    arrow-resilience = { group = "io.arrow-kt", name = "arrow-resilience", version.ref = "arrow" }
                    
                    //MVI - (Arrow, HttpResponseValidator, intercept, context MutableStateFlow(private, field syntax))
                    
                    //ApiConfig.kt
                    private object ApiConfig {
                        const val BASE_URL = "multiplatform.com"
                    }

                    internal object Endpoints {
                        const val LOGIN_URL = "${ApiConfig.BASE_URL}login"
                        const val TOP_SELLING_URL = "${ApiConfig.BASE_URL}topselling"
                        const val SALES_URL = "${ApiConfig.BASE_URL}sales"
                    }
                    
                   //NetworkModule.kt
                    val networkModule = module {
                        // 1. Provide the HttpClient using the platform-specific engine
                        single {
                            val client = HttpClient(CIO) {

                                // 1. CONTENT NEGOTIATION (JSON)
                                install(ContentNegotiation) {
                                    json(Json {
                                        ignoreUnknownKeys = true // Crucial for AniList/MAL adding new fields
                                        isLenient = true
                                        explicitNulls = false // Don't serialize nulls
                                    })
                                }

                                // 2. TIMEOUTS (Don't let the app hang forever on a bad connection)
                                install(HttpTimeout) {
                                    requestTimeoutMillis = 15000L // 15 seconds
                                    connectTimeoutMillis = 15000L
                                    socketTimeoutMillis = 15000L
                                }

                                // 3. RETRIES (Crucial for mobile networks)
                                install(HttpRequestRetry) {
                                    exponentialDelay() // Waits 1s, then 2s, then 4s between retries
                                }

                                // 4. DEFAULT REQUEST (Applies to every call)
                                install(DefaultRequest) {
                                    headers.append("Content-Type", "application/json")
                                }

                                //5. GET RESPONSE, STATUS CODE, HOST AND CUSTOM HANDLE EXCEPTION
                                HttpResponseValidator {
                                    validateResponse { response ->
                                        if(response.request.url.host == "multiplatform.com") {
                                            val statusCode = response.status.value
                                            if (statusCode in 200..299) return@validateResponse

                                            val error = runCatching { response.body<ErrorResponse>() }.getOrNull()
                                            error.let {
                                                throw DomainError.HttpError(
                                                    code = error?.code?.toIntOrNull() ?: statusCode,
                                                    message = error?.message ?: response.body()
                                                )
                                            }
                                        }
                                    }

                                    handleResponseExceptionWithRequest { exception, _ ->

                                    }
                                }

                                // LOGGING
                                /*install(Logging) {
                                    level = LogLevel.ALL // ALL is usually too noisy for production payloads
                                    logger = object : Logger {
                                        override fun log(message: String) {
                                            Napier.v(tag = "KtorClient", message = message)
                                        }
                                    }
                                }*/

                                // 6. LOGGING Ktor
                                Logging {
                                    this.logger = object : KtorLogger {
                                        private val log = KermitLogger.withTag("Ktor")
                                        override fun log(message: String) {
                                            log.i { message }
                                        }
                                    }
                                    this.level = LogLevel.INFO
                                }
                            }

                            //CUSTOM AUTH INTERCEPTOR
                            KtorAuthInterceptor.install(client)

                            client
                        }
                    }
                    
                    val appModule = module {

                        includes(networkModule)

                        single<CoroutineDispatcher> {
                            Dispatchers.Main.limitedParallelism(24)
                        }

                        single { networkRetrySchedule() } withOptions { createdAtStart() }
                        single<ResilientHttpRequester>()

                        single<VendorHttpClient>() bind VendorApi:: class
                        single<VendorContainerRepositoryImpl>() bind VendorContainerRepository:: class

                        //UseCase
                        factory<LoginUseCaseImpl>() bind LoginUseCase:: class

                        //ViewModel Persistence Compose Runtime Refresh
                        single<LoginViewModel>()

                        //ViewModel Auto Clear
                        //viewModel<TopSellingViewModel>()
                    }
                    
                    //HttpClientExtensions.kt
                    expect class UnknownHostException : Exception

                    context(raise: Raise<DomainError>)
                    suspend inline fun <reified T> HttpClient.singleAttempt(
                        noinline block: suspend HttpClient.() -> HttpResponse,
                    ): T = try {
                        val response = block()
                        response.body<T>()
                    } catch (e: CancellationException) {
                        throw e
                    } catch (e: Throwable) {
                        raise.raise(e.toDomainError())
                    }

                    suspend fun Throwable.toDomainError(): DomainError = when (this) {
                        is DomainError -> this
                        is NoConnectionException -> DomainError.NoConnection()
                        is HttpRequestTimeoutException -> DomainError.Timeout(this)
                        is ResponseException -> DomainError.HttpError(response.status.value, response.bodyAsText())
                        is UnknownHostException, is UnresolvedAddressException, is IOException -> DomainError.ConnectionError(this)
                        is SerializationException, is JsonConvertException -> DomainError.SerializationError(this)
                        else -> DomainError.UnknownError(this)
                    }
                    
                    //KtorAuthInterceptor.kt
                    internal class KtorAuthInterceptor {

                        companion object Plugin : HttpClientPlugin<Unit, KtorAuthInterceptor> {
                            override val key: AttributeKey<KtorAuthInterceptor> = io.ktor.util.AttributeKey("KtorAuthInterceptor")

                            override fun prepare(block: Unit.() -> Unit): KtorAuthInterceptor {
                                // We can't inject here directly, so we'll get it from the HttpClient configuration
                                throw IllegalStateException("Use install(KtorAuthInterceptor) { authRepository = ... }")
                            }

                            override fun install(plugin: KtorAuthInterceptor, scope: HttpClient) {

                            }

                            // Custom install method for Koin/Manual injection
                            fun install(client: HttpClient) {
                                client.plugin(HttpSend).intercept { request ->
                                    if(request.host == "multiplatform.com") {
                                        request.header("AUTH_KEY", "")
                                    }
                                    execute(request)
                                }
                            }
                        }
                    }
                    
                    //NetworkRetryPolicy.kt
                    internal fun networkRetrySchedule(): Schedule<DomainError, *> =
                        Schedule.exponential<DomainError>(200.milliseconds, factor = 2.0)
                            .and(Schedule.recurs(RETRIES))
                            .jittered()
                            .doWhile { error, _ -> error.isRetryable }

                    private const val RETRIES: Long = 3
                    
                    //ResilientHttpRequester.kt
                    internal class ResilientHttpRequester(
                        val client: HttpClient,
                        val schedule: Schedule<DomainError, *>,
                    ) {
                        /**
                         * Execute [block] on the underlying [HttpClient] and decode the response as [T].
                         *
                         * Every failure mode — network hiccup, 5xx, body parse error, timeout, offline —
                         * is expressed as [Either.Left] of the matching [DomainError] variant. No throw.
                         */
                        suspend inline fun <reified T> request(
                            noinline block: suspend HttpClient.() -> HttpResponse,
                        ): Either<DomainError, T> = schedule.retryRaise {
                            client.singleAttempt<T>(block)
                        }
                    }
                    
                    sealed class DomainError(
                        override val message: String? = null,
                        override val cause: Throwable? = null,
                    ) : Exception(message, cause) {
                        data class HttpError(val code: Int, override val message: String?) : DomainError(message)
                        data class SerializationError(override val cause: Throwable) : DomainError(cause.message, cause)
                        data class ConnectionError(override val cause: Throwable) : DomainError(cause.message, cause)
                        data class Timeout(override val cause: Throwable?) : DomainError("Request timed out", cause)
                        class NoConnection : DomainError("No network available")
                        data class UnknownError(override val cause: Throwable) : DomainError(cause.message, cause)

                        /**
                         * Retry predicate used by ResilientHttpRequester's Schedule.
                         * NoConnection is intentionally NOT retryable — it's gated *before* the request by
                         * the connectivity pre-check; waiting for reconnection is a different policy
                         * (observable via ConnectivityMonitor.observe()).
                         */
                        val isRetryable: Boolean
                            get() = when (this) {
                                is ConnectionError, is Timeout -> true
                                is HttpError -> code in 500..599 || code == 429 || code == 408
                                is SerializationError, is UnknownError, is NoConnection -> false
                            }
                    }

                    internal class NoConnectionException : IOException("No internet connection")
                    
                    //API
                    interface VendorApi {
                        suspend fun login(loginRequest: LoginRequest): Either<DomainError, LoginDto>
                        suspend fun topSelling(): Either<DomainError, List<TopSellingDto>>
                    }

                    internal class VendorHttpClient(private val requester: ResilientHttpRequester) : VendorApi {
                        override suspend fun login(loginRequest: LoginRequest): Either<DomainError, LoginDto>  = requester.request {
                            get(Endpoints.LOGIN_URL) {
                                parameter("MOBILNO", loginRequest.mobile)
                                parameter("PASSWORD", loginRequest.password)
                                parameter("APPTOKENDEVICEID", loginRequest.deviceId)
                                parameter("APPTOKEN", loginRequest.firebaseToken)
                            }
                        }

                        override suspend fun topSelling(): Either<DomainError, List<TopSellingDto>> = requester.request {
                            get(Endpoints.TOP_SELLING_URL)
                        }
                    }

                    //Repository 
                    interface VendorContainerRepository {
                        suspend fun login(loginRequest: LoginRequest) : Either<DomainError, Login>

                        suspend fun topSelling() : Either<DomainError, List<TopSelling>>
                    }
                    
                    internal class VendorContainerRepositoryImpl(private val vendorApi : VendorApi) : VendorContainerRepository {
                        override suspend fun login(loginRequest: LoginRequest): Either<DomainError, Login> = vendorApi
                            .login(loginRequest)
                            .map(LoginDto::toDomain)

                        override suspend fun topSelling(): Either<DomainError, List<TopSelling>>  = vendorApi
                            .topSelling()
                            .map { it.map(TopSellingDto::toDomain) }
                    }
                    
                    @Serializable
                    internal data class ErrorResponse(
                        @SerialName("RESPONSEMESSAGE") val message: String,
                        @SerialName("RESPONSECODE") val code: String
                    ) 
                    
                    //Mapper
                    @Serializable
                    data class LoginDto(
                        @SerialName(value ="USERID") val userId: String)
                    )
                    
                    data class Login(
                        val userId: String
                    )
                    
                    internal fun LoginDto.toDomain() = Login(userId)
                    
                    //domain
                    interface VendorContainerRepository {
                        suspend fun login(loginRequest: LoginRequest) : Either<DomainError, Login>
                        suspend fun topSelling() : Either<DomainError, List<TopSelling>>
                    }
                    
                    fun interface LoginUseCase {
                        suspend operator fun invoke(loginRequest: LoginRequest): Either<DomainError, Login>
                    }

                    internal class LoginUseCaseImpl (
                        private val vendorContainerRepository: VendorContainerRepository,
                    ) : LoginUseCase {
                        override suspend operator fun invoke(loginRequest: LoginRequest): Either<DomainError, Login> =
                            vendorContainerRepository.login(loginRequest)
                    }
                    
                    //presentation
                    @Composable
                    fun <T> ObserveAsEvents(
                        flow: Flow<T>,
                        key1: Any? = null,
                        key2: Any? = null,
                        onEvent: suspend (T) -> Unit
                    ) {
                        val lifecycleOwner = LocalLifecycleOwner.current
                        LaunchedEffect(lifecycleOwner, key1, key2) {
                            lifecycleOwner.repeatOnLifecycle(Lifecycle.State.STARTED) {
                                withContext(Dispatchers.Main.immediate) {
                                    flow.collect(onEvent)
                                }
                            }
                        }
                    }
                    
                    @Immutable
                    internal sealed interface LoginIntent {
                         object Login : LoginIntent
                         object LoginSuccess : LoginIntent
                         class LoginFailed(val message: String?) : LoginIntent
                    }
                    
                    @Stable
                    data class LoginUiState(
                        val mobileTextFieldState: TextFieldState = TextFieldState(),
                        val passwordTextFieldState: TextFieldState = TextFieldState(),

                        val canLogin: Boolean = false,
                        val isLoggingIn: Boolean = false,

                        val isLoading: Boolean = false,
                        val login: Login? = null,
                        val error: Throwable? = null
                    )
                    
                    internal class LoginViewModel(private val loginUseCase: LoginUseCase) : ViewModel() {
                        private var hasLoadedInitialData = false

                        var loginState = MutableStateFlow(LoginUiState())
                            private set

                        val loginFlow = loginState
                            .onStart {
                                if (!hasLoadedInitialData) {
                                    observeTextStates()
                                    hasLoadedInitialData = true
                                }
                            }.stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5000),
                                initialValue = LoginUiState()
                            )

                        private val eventChannel = Channel<LoginIntent>()
                        val events = eventChannel.receiveAsFlow()

                        fun onIntent(intent: LoginIntent) {
                            when (intent) {
                                LoginIntent.Login -> loginFetch()
                                LoginIntent.LoginSuccess -> {}
                                else -> {}
                            }
                        }

                        private fun loginFetch() = with(viewModelScope) {
                            if (!loginFlow.value.canLogin) return@with

                            launch {
                                loginState.update { it.copy(isLoading = true, isLoggingIn = false) }

                                val mobile = loginFlow.value.mobileTextFieldState.text.toString()
                                val password = loginFlow.value.passwordTextFieldState.text.toString()

                                loginUseCase(LoginRequest(mobile, password, "", "")).fold(
                                    ifLeft = { error ->
                                        loginFlow.value.mobileTextFieldState.clearText()
                                        loginFlow.value.passwordTextFieldState.clearText()

                                        eventChannel.send(LoginIntent.LoginFailed(error.message))

                                        loginState.update {
                                            it.copy(
                                                isLoading = false,
                                                isLoggingIn = false,
                                                error = error
                                            )
                                        }
                                    },
                                    ifRight = { response ->
                                        loginState.update {
                                            it.copy(
                                                isLoading = false,
                                                isLoggingIn = true,
                                                login = response
                                            )
                                        }

                                        with(response) {
                                            with("Session object") {
                                                loginSuccess()
                                            }
                                        }

                                        loginFlow.value.mobileTextFieldState.clearText()
                                        loginFlow.value.passwordTextFieldState.clearText()

                                        eventChannel.send(LoginIntent.LoginSuccess)
                                    }
                                )
                            }
                        }
                        
                        //buildState use more than parameter or multiple same update value
                        //https://github.com/ImChabs/DontForgetApp.git
                        private fun buildState(
                           currentState: HomeSetupState = HomeSetupState(),
                           placeName: String = currentState.placeName,
                        ): HomeSetupState {
                               return currentState.copy(
                                   placeName = placeName,
                                   hasInitializedDefaultName = hasInitializedDefaultName
                               )
                        }

                        internal val isMobileValidFlow = snapshotFlow { loginFlow.value.mobileTextFieldState.text.toString() }
                            .map { mobile -> MobileValidator.validate(mobile) }
                            .distinctUntilChanged()

                        private val isPasswordValidFlow = snapshotFlow { loginFlow.value.passwordTextFieldState.text.toString() }
                            .map { password -> PasswordValidator.validate(password) }
                            .distinctUntilChanged()

                        private val isLoggingFlow = loginFlow
                            .map { it.isLoggingIn }
                            .distinctUntilChanged()

                        private fun observeTextStates() {
                            combine(
                                isLoggingFlow, isMobileValidFlow, isPasswordValidFlow
                            ) { isLoggingFlow, isMobileValid, isPasswordValidFlow ->

                                loginState.update {
                                    it.copy(
                                        canLogin = !isLoggingFlow && isMobileValid && isPasswordValidFlow,
                                    )
                                }
                            }.launchIn(viewModelScope)
                        }

                        context(login: Login, session: String )
                        private fun loginSuccess() {
                            //Success login after session manage
                        }
                    }
                    
                    object MobileValidator {
                        private const val NUMBER_PATTERN = ""

                        fun validate(mobile: String): Boolean{
                            return mobile.isNotBlank()
                        }
                    }
                    
                    object PasswordValidator {
                        private const val NUMBER_PATTERN = ""

                        fun validate(password: String): Boolean{
                            return password.isNotBlank()
                        }
                    }
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    internal fun LoginScreen(
                    	viewModel: LoginViewModel = koinViewModel(),
                    	onNavigateUp: () -> Unit,
                    	onProductEntryUp: () -> Unit
                    ) {
                    	val state by viewModel.loginFlow.collectAsStateWithLifecycle()
                    	val localToast = LocalToastState.current
                    	val scope = rememberCoroutineScope()

                    	ObserveAsEvents(viewModel.events) { event ->
                    		when (event) {
                    			is LoginIntent.LoginSuccess -> onProductEntryUp()
                    			is LoginIntent.LoginFailed -> {
                    				scope.launch {
                                        //Toast message
                    					//event.message
                    				}
                    			}
                                else -> {}
                            }
                    	}

                    	LoginUiState(
                    		state = state,
                    		onIntent = viewModel::onIntent
                    	)
                    }

                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    private fun LoginUiState(
                    	state: LoginUiState,
                    	onIntent: (LoginIntent) -> Unit
                    ) = with(state) {
                    }
                    """.trimIndent()
            )

            //https://github.com/NsAveek/InventoryManagement.git
            KotlinCode(
                code = """
                    //MviContract.kt
                    
                    /**
                     * Marker interface for UI State in MVI pattern.
                     * Each screen defines its own State data class implementing this interface.
                     */
                    interface UiState

                    /**
                     * Marker interface for User Intents in MVI pattern.
                     * Each screen defines its own sealed interface of intents implementing this interface.
                     */
                    interface UiIntent

                    /**
                     * Marker interface for one-time side effects in MVI pattern.
                     * Effects are consumed once (navigation, snackbar, toast, etc.)
                     */
                    interface UiEffect
                    
                    //BaseViewModel.kt
                    /**
                     * Base ViewModel implementing the MVI (Model-View-Intent) pattern.
                     *
                     * @param S The UI State type
                     * @param I The Intent (user action) type
                     * @param E The Effect (one-time side effect) type
                     */
                    abstract class BaseViewModel<S : UiState, I : UiIntent, E : UiEffect>(
                        initialState: S
                    ) : ViewModel() {

                        private val _state = MutableStateFlow(initialState)
                        val state: StateFlow<S> = _state.asStateFlow()

                        private val _effect = Channel<E>(Channel.BUFFERED)
                        val effect = _effect.receiveAsFlow()

                        private val _intent = MutableSharedFlow<I>()

                        val currentState: S get() = _state.value

                        init {
                            viewModelScope.launch {
                                _intent.collect { intent ->
                                    handleIntent(intent)
                                }
                            }
                        }

                        /**
                         * Dispatch an intent from the UI to be processed by the ViewModel.
                         */
                        fun sendIntent(intent: I) {
                            viewModelScope.launch {
                                _intent.emit(intent)
                            }
                        }

                        /**
                         * Override to handle each intent and update state or emit effects accordingly.
                         */
                        protected abstract fun handleIntent(intent: I)

                        /**
                         * Update the current state using a reducer function.
                         */
                        protected fun updateState(reducer: S.() -> S) {
                            _state.value = _state.value.reducer()
                        }

                        /**
                         * Emit a one-time side effect (navigation, toast, etc.)
                         */
                        protected fun emitEffect(effect: E) {
                            viewModelScope.launch {
                                _effect.send(effect)
                            }
                        }
                    }
                    
                    class CartViewModel(
                        private val cartManager: CartManager,
                    ) : BaseViewModel<CartContract.State, CartContract.Intent, CartContract.Effect>(
                        initialState = CartContract.State()
                    ) {
                        init {
                            viewModelScope.launch {
                                cartManager.items.collect { items ->
                                    updateState { copy(items = items, totalAmount = items.sumOf { it.totalPrice }) }
                                }
                            }
                        }

                        override fun handleIntent(intent: CartContract.Intent) {
                            when (intent) {
                                is CartContract.Intent.RemoveItem -> cartManager.removeItem(intent.productId)
                                is CartContract.Intent.CheckoutClicked -> {
                                    if (currentState.items.isNotEmpty()) {
                                        emitEffect(CartContract.Effect.NavigateToPayment)
                                    }
                                }
                                is CartContract.Intent.GoBack -> emitEffect(CartContract.Effect.NavigateBack)
                            }
                        }
                    }
                    
                    object CartContract {
                        data class State(
                            val items: List<CartItem> = emptyList(),
                            val totalAmount: Double = 0.0,
                        ) : UiState

                        sealed interface Intent : UiIntent {
                            data class RemoveItem(val productId: String) : Intent
                            data object CheckoutClicked : Intent
                            data object GoBack : Intent
                        }

                        sealed interface Effect : UiEffect {
                            data object NavigateToPayment : Effect
                            data object NavigateBack : Effect
                        }
                    }
                    
                    data class CartItem(
                        val product: Product,
                        val quantity: Int,
                    ) {
                        val totalPrice: Double get() = product.price * quantity
                    }
                    """.trimIndent()
            )

            //https://github.com/darkpandawarrior/Mileway/releases
            KotlinCode(
                code = """
                    //BaseViewModel.kt
                    /**
                     * MVI base. `S` = UiState, `E` = Effect, `A` = user Action.
                     *
                     * Two effect channels with distinct delivery contracts:
                     * - [emitEffect] → a buffered [Channel] consumed **exactly once** by the screen's `LaunchedEffect`.
                     *   Use for navigation, toasts, one-shot dialogs, things that must fire once and never replay.
                     * - [emitBroadcast] → a [SharedFlow] (no replay, drops oldest on overflow) that **survives config
                     *   change** and is delivered to whatever collector is active. Use for fire-and-forget signals where
                     *   a missed emission is acceptable but new collectors should still receive subsequent ones.
                     */
                    abstract class BaseViewModel<S : Any, E : Any, A : Any>(initial: S) : ViewModel() {
                        private val _state = MutableStateFlow(initial)
                        val state: StateFlow<S> = _state.asStateFlow()

                        private val _effect = Channel<E>(Channel.BUFFERED)
                        val effect: Flow<E> = _effect.receiveAsFlow()

                        private val _broadcast =
                            MutableSharedFlow<E>(
                                replay = 0,
                                extraBufferCapacity = 8,
                                onBufferOverflow = BufferOverflow.DROP_OLDEST,
                            )
                        protected val broadcast: SharedFlow<E> = _broadcast.asSharedFlow()

                        protected val currentState: S get() = _state.value

                        protected fun setState(reducer: S.() -> S) {
                            _state.value = _state.value.reducer()
                        }

                        /** Deliver once to the active screen collector (navigation / toast / one-shot dialog). */
                         fun emitEffect(effect: E) {
                            viewModelScope.launch { _effect.send(effect) }
                        }

                        /** Deliver to current collectors; survives config change, no replay. */
                        protected fun emitBroadcast(effect: E) {
                            _broadcast.tryEmit(effect)
                        }

                        abstract fun onAction(action: A)
                    }
                    
                    
                    /** Sealed state for any screen that loads data. Drives the UI without boolean soup. */
                    sealed interface ScreenState<out T> {
                        data object Loading : ScreenState<Nothing>

                        data object Empty : ScreenState<Nothing>

                        data object NoNetwork : ScreenState<Nothing>

                        data class Error(val message: UiText, val cause: Throwable? = null) : ScreenState<Nothing>

                        /** Loaded content. [isStale] marks data shown from cache while a refresh is in flight. */
                        data class Content<T>(val data: T, val isStale: Boolean = false) : ScreenState<T>
                    }

                    val <T> ScreenState<T>.dataOrNull: T?
                        get() = (this as? ScreenState.Content)?.data

                    val ScreenState<*>.isLoading: Boolean
                        get() = this is ScreenState.Loading

                    /** Maps the content payload while preserving the non-content branches and [Content.isStale]. */
                    inline fun <T, R> ScreenState<T>.map(transform: (T) -> R): ScreenState<R> =
                        when (this) {
                            is ScreenState.Content -> ScreenState.Content(transform(data), isStale)
                            is ScreenState.Loading -> ScreenState.Loading
                            is ScreenState.Empty -> ScreenState.Empty
                            is ScreenState.NoNetwork -> ScreenState.NoNetwork
                            is ScreenState.Error -> this
                        }

                    /** Runs [block] only when content is present; returns the state unchanged for chaining. */
                    inline fun <T> ScreenState<T>.onContent(block: (T) -> Unit): ScreenState<T> {
                        if (this is ScreenState.Content) block(data)
                        return this
                    }

                    /** Content payload or [default] for every other branch. */
                    fun <T> ScreenState<T>.contentOrElse(default: T): T = dataOrNull ?: default

                    /** Convenience constructors. */
                    fun <T> T.asContent(isStale: Boolean = false): ScreenState<T> = ScreenState.Content(this, isStale)

                    fun errorState(message: String): ScreenState<Nothing> = ScreenState.Error(UiText.Static(message))
                    
                    /**
                     * Single switchboard for any data-loading screen. Replaces ad-hoc
                     * `if (isLoading) Shimmer() else Content()` patterns with one [ScreenState] consumer so loading,
                     * empty, no-network and error are handled consistently everywhere.
                     */
                    @Composable
                    fun <T> ScreenStateContent(
                        state: ScreenState<T>,
                        modifier: Modifier = Modifier,
                        onRetry: (() -> Unit)? = null,
                        loading: @Composable () -> Unit = { DefaultLoadingState() },
                        empty: @Composable () -> Unit = { DefaultEmptyState() },
                        error: @Composable (UiText) -> Unit = { DefaultErrorState(it, onRetry) },
                        content: @Composable (T) -> Unit,
                    ) {
                        Box(modifier = modifier) {
                            when (state) {
                                is ScreenState.Loading -> loading()
                                is ScreenState.Empty -> empty()
                                is ScreenState.NoNetwork ->
                                    DefaultErrorState(
                                        UiText.Static("You're offline. Check your connection and try again."),
                                        onRetry,
                                        icon = Icons.Outlined.CloudOff,
                                    )
                                is ScreenState.Error -> error(state.message)
                                is ScreenState.Content -> content(state.data)
                            }
                        }
                    }

                    @Composable
                    fun DefaultLoadingState(modifier: Modifier = Modifier) {
                        Box(
                            modifier = modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center,
                        ) {
                            CircularProgressIndicator()
                        }
                    }

                    /** Skeleton list for list-shaped screens; thin wrapper over the shared list shimmer. */
                    @Composable
                    fun ShimmerList(
                        itemCount: Int = 5,
                        modifier: Modifier = Modifier,
                    ) {
                        Box(modifier = modifier.fillMaxWidth()) {
                            UnifiedListShimmer(itemCount = itemCount)
                        }
                    }

                    @Composable
                    fun DefaultEmptyState(
                        modifier: Modifier = Modifier,
                        icon: ImageVector = Icons.Outlined.Inbox,
                        title: String = stringResource(Res.string.core_empty_default),
                        subtitle: String? = null,
                        ctaLabel: String? = null,
                        onCta: (() -> Unit)? = null,
                    ) {
                        Column(
                            modifier =
                                modifier
                                    .fillMaxSize()
                                    .padding(32.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center,
                        ) {
                            Box(
                                modifier =
                                    Modifier
                                        .size(64.dp)
                                        .background(
                                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                                            CircleShape,
                                        ),
                                contentAlignment = Alignment.Center,
                            ) {
                                Icon(
                                    imageVector = icon,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                                    modifier = Modifier.size(32.dp),
                                )
                            }
                            Text(
                                text = title,
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.onSurface,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.padding(top = 16.dp),
                            )
                            if (subtitle != null) {
                                Text(
                                    text = subtitle,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center,
                                    modifier = Modifier.padding(top = 8.dp),
                                )
                            }
                            if (ctaLabel != null && onCta != null) {
                                OutlinedButton(
                                    onClick = onCta,
                                    modifier = Modifier.padding(top = 20.dp),
                                ) {
                                    Text(ctaLabel)
                                }
                            }
                        }
                    }

                    @Composable
                    fun DefaultErrorState(
                        message: UiText,
                        onRetry: (() -> Unit)? = null,
                        modifier: Modifier = Modifier,
                        icon: ImageVector = Icons.Outlined.ErrorOutline,
                    ) {
                        Column(
                            modifier =
                                modifier
                                    .fillMaxSize()
                                    .padding(32.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center,
                        ) {
                            Box(
                                modifier =
                                    Modifier
                                        .size(64.dp)
                                        .background(
                                            MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.5f),
                                            CircleShape,
                                        ),
                                contentAlignment = Alignment.Center,
                            ) {
                                Icon(
                                    imageVector = icon,
                                    contentDescription = stringResource(Res.string.core_cd_error),
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(32.dp),
                                )
                            }
                            Text(
                                text = message.asString(),
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurface,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.padding(top = 16.dp),
                            )
                            if (onRetry != null) {
                                OutlinedButton(
                                    onClick = onRetry,
                                    modifier = Modifier.padding(top = 20.dp),
                                ) {
                                    Text(stringResource(Res.string.core_try_again))
                                }
                            }
                        }
                    }
                    
                    class CreateParkingViewModel() : BaseViewModel<CreateParkingUiState, CreateParkingEffect, CreateParkingAction>(CreateParkingUiState()) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Does MVI make my modular code more complex?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVI introduces slightly more boilerplate code (defining Intent, State, and SideEffect classes) but drastically reduces complexity in the long run. In a modular structure, the explicitly defined contracts (ProfileContract.kt) ensure that developers in the UI module can only interact with the ViewModel in predefined ways, preventing ad-hoc logic and promoting better testability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "UiState - (Action → data class = flexible data holder for results/events)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Represents everything the screen needs to render at a given time. Exposing the state as an immutable, observable stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    sealed interface UserAction {
                        data class Success(val users: List<User>) : UserAction
                        data class Error(val message: String) : UserAction
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "UiIntent - (Intent → sealed interface = closed set of possible user actions)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "User actions sent from UI to ViewModel (clicks, inputs, refresh, etc). All user actions are represented as data streams, often sealed classes or interfaces.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data objects have toString() functions implemented like data classes so that you don’t need to override toString() in objects anymore.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Choose data object / data class
                    
                    sealed interface UsersAction {
                        data object ScreenStarted : UsersAction
                        data object Refresh : UsersAction
                        data object Retry : UsersAction
                        data class DeleteUser(val userId: UserId) : UsersAction
                        data object UndoDelete : UsersAction
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "If you need to synchronise your data from different source into same state you should",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When structuring our ViewState, we often need to merge independent streams (e.g., user profile data, a local database list, and network status). combine automatically emits a new object whenever any of its upstream flows emit a value, merging the most recent data from all of them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, standard Flows are Cold, they restart their collection process from scratch for every new subscriber. To make them Jetpack Compose-ready and lifecycle-aware, we use stateIn to transform that combined stream into a Hot StateFlow.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    data class HomeViewState(
                        val isLoading: Boolean = true,
                        val userName: String = "",
                        val items: List<String> = emptyList(),
                        val isOffline: Boolean = false
                    )
                    
                    val viewState: StateFlow<HomeViewState> = combine(
                        userRepository. getUserProfile(),
                        itemRepository. getLatestItems(),
                        networkMonitor.isOnline
                    ) { user, items, isOnline ->
                        HomeViewState(
                            isLoading = false,
                            userName = user.name,
                            items = items,
                            isOffline = !isOnline
                        }.stateIn(
                            scope = viewModelScope,
                            started = SharingStarted. WhileSubscribed (5_000),
                            initialValue = HomeViewState(isLoading = true)
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "UiEffect",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One-time events that should not persist in state:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Navigation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Snackbar",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Toast",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dialog",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Deep links",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }

            WebImage(
                src = Resources.Images.MVI_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "mvi"
            )

            SpanText(
                text = "Separate properties & UiState pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Our view is consistently represented in one property.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We do not need to deal with properties that are currently not displayed (like news, when we are still loading).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We update our views using copy, what eliminates dependencies between updated properties (like when we first change a, and then b, and we accidentally calculate new state of b based on the new state of a, instead of on the old one).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In complex views, UiState can become really big and complex.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using UiState makes it harder (but not impossible) to optimise recomposition by moving reads directly to where they are needed (for that, the most efficient way is defining State in view model).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UiState often duplicates some classes from domain (what adds boilerplate, but some consider beneficial as it defines ui-specific representation of those classes).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/YuKongA/Updater-KMP
            KotlinCode(
                code = """
                    sealed interface LoginState {
                        data object NotLoggedIn : LoginState
                        data class LoggedIn(val loginData: DataHelper.LoginData) : LoginState
                        data class Expired(val loginData: DataHelper.LoginData) : LoginState
                    }

                    sealed interface UiEvent {
                        data class ShowMessage(val message: String, val duration: Long = 1000L) : UiEvent
                    }

                    sealed interface LoginEvent {
                        data class AccountChanged(val value: String) : LoginEvent
                        data class PasswordChanged(val value: String) : LoginEvent
                        data class GlobalChanged(val value: Boolean) : LoginEvent
                        data class SavePasswordChanged(val value: String) : LoginEvent
                        data class TicketChanged(val value: String) : LoginEvent
                        data class VerificationRequested(val value: Boolean) : LoginEvent
                        data class Select2FAMethod(val flag: Int) : LoginEvent
                        data object LoginClicked : LoginEvent
                        data object LogoutClicked : LoginEvent
                        data object Submit2FA : LoginEvent
                        data object CancelTicket : LoginEvent
                        data object DismissDialog : LoginEvent
                    }

                    data class AppUiState(
                        val deviceName: String = "",
                        val codeName: String = "",
                        val deviceRegion: String = "Default (CN)",
                        val deviceCarrier: String = "Default (Xiaomi)",
                        val androidVersion: String = "16.0",
                        val systemVersion: String = "",
                        val loginState: LoginState = LoginState.NotLoggedIn,
                        val showLoginDialog: Boolean = false,
                        val loginAccount: String = "",
                        val loginPassword: String = "",
                        val loginGlobal: Boolean = false,
                    )
                    """.trimIndent()
            )

            //https://androidmeda.medium.com
            KotlinCode(
                code = """
                    data class ArticlesUiState(
                        val isLoading: Boolean = false,
                        val items: List<Article> = emptyList(),
                        val error: String? = null
                    )

                    sealed interface ArticlesIntent {
                        data object Load : ArticlesIntent
                        data object Retry : ArticlesIntent
                        data class FilterChanged(val query: String) : ArticlesIntent
                    }

                    class ArticlesViewModel(
                        private val repository: ArticlesRepository
                    ) : ViewModel() {
                        private val _state = MutableStateFlow(ArticlesUiState())
                        val state: StateFlow<ArticlesUiState> = _state
                        fun onIntent(intent: ArticlesIntent) {
                            when (intent) {
                                ArticlesIntent.Load,
                                ArticlesIntent.Retry -> loadArticles()
                                is ArticlesIntent.FilterChanged -> {
                                    _state.update {
                                        it.copy(
                                            items = it.items.filter { article ->
                                                article.title.contains(intent.query, ignoreCase = true)
                                            }
                                        )
                                    }
                                }
                            }
                        }
                        
                        private fun loadArticles() {
                            viewModelScope.launch {
                                _state.update { it.copy(isLoading = true, error = null) }
                                runCatching { repository.getArticles() }
                                    .onSuccess { items ->
                                        _state.update { it.copy(isLoading = false, items = items) }
                                    }
                                    .onFailure { error ->
                                        _state.update {
                                            it.copy(
                                                isLoading = false,
                                                error = error.message ?: "Something went wrong"
                                            )
                                        }
                                    }
                            }
                        }
                    }
                    
                    /*
                    viewModelScope.launch {
                        val result = runCatching {
                            repository.getArticles()
                        }.onFailure { e ->
                            if (e is CancellationException) throw e
                        }
                    }
                    */
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //UiState pattern (MODERN WAY)
                    class NewsViewModel {
                        private val _viState = MutableStateFlow<NewsUiState>(NewsUiState.Loading)
                        val viState = _viState.asStateFlow()
                    }
                    
                    sealed interface NewsUiState {
                        data object Loading : NewsUiState 
                        data class ShowNews(val news: List<News>) : NewsUiState
                    }
                    
                    @Composable
                    fun NewsScreen(viewModel: NewsViewModel) {
                        val viState = viewModel.viState.collectAsStateWithLifecycle()
                        when (val state = UiState.value) {
                            NewsUiState.Loading -> ProgressScreen()
                            is NewsUiState.ShowNews -> NewsList(state.news)
                        }
                    }
                    
                    //Separate properties
                     class NewsViewModel {
                        private val _viState = MutableStateFlow<NewsUiState>(NewsUiState.Loading)
                        val viState = _viState.asStateFlow()
                        
                        private val _loading = MutableStateFlow( value: false)
                        val loading = _loading.asStateFlow()
                    }
                    
                    @Composable
                    fun NewsScreen (viewModel: NewsViewModel) {
                        val loading = viewModel.loading.collectAsStateWithLifecycle()
                        val news = viewModel.news.collectAsStateWithLifecycle()
                        
                        if (loading.value) {
                            ProgressScreen()
                        } else {
                            NewsList(news.value)
                        }
                    }    
                    """.trimIndent()
            )

            SpanText(
                text = "Single UiState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using a single UiState data class is one of the most common and effective patterns in modern Android development. It works especially well with MVI, making the screen state predictable and easy to manage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, when using a single state object, it's important to understand how Jetpack Compose handles recomposition. Compose doesn't re-render because state changed - it recomposes when the parameters of a composable change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If we pass the entire UiState into many composables, even a small unrelated change inside that object can trigger recomposition in places that don't actually need it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Simple 
                    
                    class ShoppingCartViewModel : ViewModel() {
                        private val _cartState = MutableStateFlow(CartState())
                        val cartState = _cartState.asStateFlow()
                        fun addItem(item: Product) {
                            _cartState.update { currentState ->
                                val existingItem = currentState.items.find { it.product.id == item.id }
                                if (existingItem != null) {
                                    // Increase quantity
                                    currentState.copy(
                                        items = currentState.items.map { cartItem ->
                                            if (cartItem.product.id == item.id) {
                                                cartItem.copy(quantity = cartItem.quantity + 1)
                                            } else {
                                                cartItem
                                            }
                                        }
                                    )
                                } else {
                                    // Add new item
                                    currentState.copy(
                                        items = currentState.items + CartItem(item, 1)
                                    )
                                }
                            }
                        }

                        fun removeItem(productId: String) {
                            _cartState.update { currentState ->
                                currentState.copy(
                                    items = currentState.items.filter { it.product.id != productId }
                                )
                            }
                        }
                    }
                    
                    data class CartState(
                        val items: List<CartItem> = emptyList()
                    ) {
                        val totalPrice: Double
                            get() = items.sumOf { it.product.price * it.quantity }
                        val itemCount: Int
                            get() = items.sumOf { it.quantity }
                    }
                    data class CartItem(
                        val product: Product,
                        val quantity: Int
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    data class CounterState(
                        val count: Int = 0
                    )
                    
                    //Create the Intents (User Actions)
                    sealed interface CounterIntent {
                        data object Increment : CounterIntent
                        data object Decrement : CounterIntent
                        data object Reset : CounterIntent
                    }
                    
                    //ViewModel (Keeps the current state, Listens for intents, Creates new states)
                    class CounterViewModel : ViewModel() {
                        // Private — the UI cannot change this directly
                        private val _state = mutableStateOf(CounterState())
                        // Public — the UI can only READ this
                        val state: State<CounterState> = _state
                       
                        // Handle every user action here
                        fun onIntent(intent: CounterIntent) {
                            when (intent) {
                                is CounterIntent.Increment -> {
                                    _state.value = _state.value.copy(
                                        count = _state.value.count + 1
                                    )
                                }
                                is CounterIntent.Decrement -> {
                                    _state.value = _state.value.copy(
                                        count = _state.value.count - 1
                                    )
                                }
                                is CounterIntent.Reset -> {
                                    _state.value = CounterState() // Back to default
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun CounterScreen(viewModel: CounterViewModel = viewModel()) {
                        val state by viewModel.state
                        
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = "Count: ${state.count}",
                                fontSize = 24.sp
                            )
                            Row {
                                Button(onClick = { viewModel.onIntent(CounterIntent.Decrement) }) {
                                    Text("-")
                                }
                                Button(onClick = { viewModel.onIntent(CounterIntent.Increment) }) {
                                    Text("+")
                                }
                            }
                            Button(onClick = { viewModel.onIntent(CounterIntent.Reset) }) {
                                Text("Reset")
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Common ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(bottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/RamadanElSayed/MovieApp.git
            KotlinCode(
                code = """
                    abstract class BaseViewModel<S : UiState, I : Intent, E : Effect>(
                        initialState: S,
                        private val reducer: Reducer<S, I>,
                    ) : ViewModel() {
                        private val _state = MutableStateFlow(initialState)
                        val state: StateFlow<S> = _state.asStateFlow()

                        private val _effect = Channel<E>(Channel.BUFFERED)
                        val effect: Flow<E> = _effect.receiveAsFlow()

                        val currentState: S get() = _state.value

                        fun sendIntent(intent: I) {
                            _state.update { reducer.reduce(it, intent) }
                            handleIntent(intent)
                        }

                        protected open fun handleIntent(intent: I) = Unit

                        protected fun setState(transform: (S) -> S) = _state.update(transform)

                        protected fun sendEffect(effect: E) {
                            viewModelScope.launch { _effect.send(effect) }
                        }
                    }
                    
                    interface UiState

                    interface Intent

                    interface Effect

                    fun interface Reducer<S : UiState, I : Intent> {
                        fun reduce(state: S, intent: I): S
                    }
                    
                    class FavoritesViewModel(
                        private val observeFavorites: ObserveFavoriteMoviesUseCase,
                        private val toggleFavorite: ToggleFavoriteUseCase,
                    ) : BaseViewModel<FavoritesState, FavoritesIntent, FavoritesEffect>(
                        initialState = FavoritesState(),
                        reducer = FavoritesReducer(),
                    ) {
                    }
                    
                    data class FavoritesState(
                        val isLoading: Boolean = true,
                        val items: List<FavoriteUiModel> = emptyList(),
                    ) : UiState {
                        val isEmpty: Boolean get() = !isLoading && items.isEmpty()
                    }
                    
                    class FavoritesReducer : Reducer<FavoritesState, FavoritesIntent> {
                        override fun reduce(state: FavoritesState, intent: FavoritesIntent): FavoritesState =
                            when (intent) {
                                FavoritesIntent.Load -> state.copy(isLoading = true)
                                is FavoritesIntent.ItemsLoaded -> state.copy(isLoading = false, items = intent.items)
                                is FavoritesIntent.Remove,
                                is FavoritesIntent.OpenDetails,
                                -> state
                            }
                    }
                    
                    sealed interface FavoritesIntent : Intent {
                        data object Load : FavoritesIntent
                        data class ItemsLoaded(val items: List<FavoriteUiModel>) : FavoritesIntent
                        data class Remove(val movieId: Int) : FavoritesIntent
                        data class OpenDetails(val movieId: Int) : FavoritesIntent
                    }
                    
                    @Composable
                    fun FavoritesRoute(
                        onOpenDetails: (Int) -> Unit,
                        viewModel: FavoritesViewModel = koinViewModel(),
                    ) {
                        val state by viewModel.state.collectAsStateWithLifecycle()
                        FavoritesScreen(state = state, onIntent = viewModel::sendIntent)
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    internal fun FavoritesScreen(
                        state: FavoritesState,
                        onIntent: (FavoritesIntent) -> Unit,
                    ) {
                    }
                    """.trimIndent()
            )

            //https://github.com/skymansandy/kurlclient.git
            KotlinCode(
                code = """
                    compilerOptions {
                      freeCompilerArgs.add("-Xexplicit-backing-fields")
                    }
    
                    interface UiState
                    interface UiEvent
                    interface UiEffect
                    
                    abstract class MviViewModel<State : UiState, Event : UiEvent, Effect : UiEffect> : ViewModel() {

                        val state: StateFlow<State>
                            field = MutableStateFlow(createInitialState())

                        val effects: SharedFlow<Effect>
                            field = MutableSharedFlow<Effect>()

                        val events: SharedFlow<Event>
                            field = MutableSharedFlow<Event>()

                        fun setState(reducer: State.() -> State) {
                            state.update {
                                val newState = it.reducer()
                                newState
                            }
                        }

                        fun setEffect(effect: Effect) {
                            effects.tryEmit(effect)
                        }

                        fun setEvent(event: Event) {
                            viewModelScope.launch {
                                events.emit(event)
                            }
                        }

                        abstract fun onEvent(event: Event)

                        abstract fun createInitialState(): State

                        init {
                            viewModelScope.launch {
                                events.collect {
                                    onEvent(it)
                                }
                            }
                        }
                    }
                    
                    @KoinViewModel
                    internal class CollectionsViewModel() :
                    MviViewModel<CollectionsState, CollectionsEvent, CollectionsEffect>() {
                        internal class CollectionsScreenContract {

                            data class CollectionsState(
                                val allFolders: List<CollectionFolder> = emptyList(),
                                val allRequests: List<SavedRequest> = emptyList(),
                                val expandedFolderIds: Set<Long> = emptySet(),
                                val folderPaths: Map<Long, String> = emptyMap(),
                                val searchQuery: String = "",
                            ) : UiState {

                                sealed interface TreeItem {
                                    data class Folder(
                                        val folder: CollectionFolder,
                                        val depth: Int,
                                        val isExpanded: Boolean,
                                    ) : TreeItem

                                    data class Request(
                                        val request: SavedRequest,
                                        val depth: Int,
                                    ) : TreeItem
                                }
                            }

                            sealed interface CollectionsEvent : UiEvent {
                                data class ToggleFolder(val id: Long) : CollectionsEvent
                                data class SetSearchQuery(val query: String) : CollectionsEvent
                                data class CreateFolder(val name: String, val parentId: Long?) : CollectionsEvent
                                data class MoveFolder(val id: Long, val newParentId: Long?) : CollectionsEvent
                                data class MoveRequest(val id: Long, val newFolderId: Long?) : CollectionsEvent
                                data class DeleteFolder(val id: Long) : CollectionsEvent
                                data class DeleteRequest(val id: Long) : CollectionsEvent
                                data class DuplicateRequest(val id: Long) : CollectionsEvent
                            }

                            sealed interface CollectionsEffect : UiEffect
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP
            KotlinCode(
                code = """
                    abstract class MviViewModel<Event, State>(defaultState: State) : ViewModel() {

                        protected val events = MutableSharedFlow<Event>()

                        var state by mutableStateOf(defaultState)
                            protected set

                        fun dispatch(event: Event) {
                            viewModelScope.launch { events.emit(event) }
                        }

                        protected inline fun <reified SpecificEvent : Event> on(
                            crossinline handle: suspend (event: SpecificEvent) -> Unit,
                        ) {
                            events
                                .filterIsInstance<SpecificEvent>()
                                .map { runCatching { handle(it) } }
                                .map { it.exceptionOrNull() }
                                .filterNotNull()
                                .onEach(this::onError)
                                .launchIn(viewModelScope)
                        }

                        protected open fun onError(throwable: Throwable) = Unit
                    }
                """.trimIndent()
            )

            //https://github.com/YouXiang8727/StreamletMultiplatform
            KotlinCode(
                code = """
                    interface UiState

                    interface UiEvent

                    interface UiEffect

                    abstract class Reducer<S : UiState, E : UiEvent> {
                        abstract fun reduce(state: S, event: E): S
                    }

                    abstract class MviViewModel<S: UiState, E: UiEvent, F: UiEffect>(
                        initialState: S,
                        private val reducer: Reducer<S, E>
                    ): ViewModel() {
                        private val _state: MutableStateFlow<S> = MutableStateFlow(initialState)
                        val state: StateFlow<S> = _state.asStateFlow()

                        private val _event: MutableSharedFlow<E> = MutableSharedFlow(
                            replay = 0,
                            extraBufferCapacity = 64
                        )

                        private val _effect: MutableSharedFlow<F> = MutableSharedFlow(
                            replay = 0,
                            extraBufferCapacity = 1
                        )
                        val effect: SharedFlow<F> = _effect.asSharedFlow()

                        fun dispatch(event: E) {
                            _event.tryEmit(event)
                        }

                        fun dispatch(effect: F) {
                            _effect.tryEmit(effect)
                        }

                        init {
                            viewModelScope.launch {
                                _event.collect { event ->
                                    _state.update {
                                        reducer.reduce(state = _state.value, event = event)
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/manu0396/PetFinderComposeMultiplatform.git
            KotlinCode(
                code = """
                    class LoginContract {
                        data class State(
                            val email: String = "",
                            val password: String = "",
                            val mode: AuthMode = AuthMode.LOGIN,
                            val isLoading: Boolean = false,
                            val error: String? = null,
                            val isSuccess: Boolean = false
                        )

                        // 2. What the user can DO (Intents)
                        sealed interface Event {
                            data class EmailChanged(val email: String) : Event
                            data class PasswordChanged(val password: String) : Event
                            data object ToggleMode : Event
                            data object LoginClicked : Event
                            data object ConsumeSuccess : Event
                        }

                        // 3. One-time side effects (Snackbars, Navigations)
                        sealed interface Effect {
                            data class ShowError(val message: String) : Effect
                        }

                        enum class AuthMode { LOGIN, REGISTER }
                    }
                    """.trimIndent()
            )

            //https://abifarhan.medium.com/the-20-jetpack-compose-interview-questions-that-separate-seniors-from-everyone-else-2026-edition-a53a3d82265b
            KotlinCode(
                code = """
                    // MVI = Model-View-Intent (Unidirectional data flow)

                    // 1️⃣ Define State (what user sees)
                    data class ProductScreenState(
                        val product: Product? = null,
                        val isLoading: Boolean = false,
                        val error: String? = null,
                        val selectedVariant: ProductVariant? = null,
                        val cartCount: Int = 0
                    )

                    // 2️⃣ Define Intent (what user does)
                    sealed class ProductIntent {
                        data class LoadProduct(val id: String) : ProductIntent()
                        data class SelectVariant(val variant: ProductVariant) : ProductIntent()
                        object AddToCart : ProductIntent()
                        object Retry : ProductIntent()
                    }

                    // 3️⃣ Define Side Effects (one-time events)
                    sealed class ProductEffect {
                        data class ShowToast(val message: String) : ProductEffect()
                        data class NavigateToCart(val productId: String) : ProductEffect()
                        object ShowError : ProductEffect()
                    }

                    // 4️⃣ ViewModel implements MVI
                    @HiltViewModel
                    class ProductViewModel @Inject constructor(
                        private val repository: ProductRepository,
                        private val savedStateHandle: SavedStateHandle
                    ) : ViewModel() {
                        
                        // State
                        private val _state = MutableStateFlow(ProductScreenState())
                        val state = _state.asStateFlow()
                        
                        // Side effects
                        private val _effect = Channel<ProductEffect>()
                        val effect = _effect.receiveAsFlow()
                        
                        init {
                            handleIntent(ProductIntent.LoadProduct(savedStateHandle.get("productId")!!))
                        }
                        
                        // Single entry point for all user actions
                        fun handleIntent(intent: ProductIntent) {
                            when (intent) {
                                is ProductIntent.LoadProduct -> loadProduct(intent.id)
                                is ProductIntent.SelectVariant -> selectVariant(intent.variant)
                                ProductIntent.AddToCart -> addToCart()
                                ProductIntent.Retry -> retry()
                            }
                        }
                        
                        private fun loadProduct(id: String) {
                            viewModelScope.launch {
                                _state.update { it.copy(isLoading = true, error = null) }
                                
                                repository.getProduct(id)
                                    .onSuccess { product ->
                                        _state.update {
                                            it.copy(
                                                product = product,
                                                isLoading = false,
                                                selectedVariant = product.variants.firstOrNull()
                                            )
                                        }
                                    }
                                    .onFailure { error ->
                                        _state.update {
                                            it.copy(isLoading = false, error = error.message)
                                        }
                                        _effect.send(ProductEffect.ShowError)
                                    }
                            }
                        }
                        
                        private fun addToCart() {
                            viewModelScope.launch {
                                val product = _state.value.product ?: return@launch
                                val variant = _state.value.selectedVariant ?: return@launch
                                
                                repository.addToCart(product, variant)
                                    .onSuccess {
                                        _state.update { it.copy(cartCount = it.cartCount + 1) }
                                        _effect.send(ProductEffect.ShowToast("Added to cart"))
                                    }
                            }
                        }
                        
                        private fun selectVariant(variant: ProductVariant) {
                            _state.update { it.copy(selectedVariant = variant) }
                        }
                        
                        private fun retry() {
                            _state.value.product?.let {
                                handleIntent(ProductIntent.LoadProduct(it.id))
                            }
                        }
                    }

                    // 5️⃣ Composable consumes MVI
                    @Composable
                    fun ProductScreen(
                        viewModel: ProductViewModel = hiltViewModel()
                    ) {
                        val state by viewModel.state.collectAsState()
                        val snackbarHostState = remember { SnackbarHostState() }
                        
                        // Handle side effects
                        LaunchedEffect(Unit) {
                            viewModel.effect.collect { effect ->
                                when (effect) {
                                    is ProductEffect.ShowToast -> {
                                        snackbarHostState.showSnackbar(effect.message)
                                    }
                                    is ProductEffect.NavigateToCart -> {
                                        // Navigate
                                    }
                                    ProductEffect.ShowError -> {
                                        snackbarHostState.showSnackbar("Error loading product")
                                    }
                                }
                            }
                        }
                        
                        Scaffold(
                            snackbarHost = { SnackbarHost(snackbarHostState) }
                        ) { padding ->
                            when {
                                state.isLoading -> LoadingScreen()
                                state.error != null -> ErrorScreen(
                                    message = state.error!!,
                                    onRetry = { viewModel.handleIntent(ProductIntent.Retry) }
                                )
                                state.product != null -> ProductContent(
                                    product = state.product!!,
                                    selectedVariant = state.selectedVariant,
                                    onVariantSelect = { variant ->
                                        viewModel.handleIntent(ProductIntent.SelectVariant(variant))
                                    },
                                    onAddToCart = {
                                        viewModel.handleIntent(ProductIntent.AddToCart)
                                    }
                                )
                            }
                        }
                    }

                    // Benefits of MVI:
                    // ✅ Predictable state updates
                    // ✅ Easy to test (pure functions)
                    // ✅ Time-travel debugging
                    // ✅ Single source of truth
                    // ✅ Clear separation of concerns
                    """.trimIndent()
            )

            //https://abifarhan.medium.com/the-20-jetpack-compose-interview-questions-that-separate-seniors-from-everyone-else-2026-edition-a53a3d82265b
            KotlinCode(
                code = $$"""
                    // ═══════════════════════════════════════════════════════════════
                    // PROJECT ARCHITECTURE
                    // ═══════════════════════════════════════════════════════════════
                    //
                    // :app (Application module)
                    //   └─ Depends on all feature modules
                    //
                    // :feature:products (Feature module)
                    //   ├─ :feature:products:ui (Compose UI)
                    //   ├─ :feature:products:domain (Business logic)
                    //   └─ :feature:products:data (Data layer)
                    //
                    // :core:ui (Shared UI components)
                    //   └─ Design system, common composables
                    //
                    // :core:network (Networking)
                    // :core:database (Local storage)
                    // :core:analytics (Analytics)
                    //
                    // ═══════════════════════════════════════════════════════════════

                    // 1️⃣ :core:ui - Design System Module
                    // ═══════════════════════════════════════════════════════════════
                    // build.gradle.kts (:core:ui)
                    dependencies {
                        api(compose.material3)
                        api(compose.ui)
                        api(compose.uiToolingPreview)
                    }

                    // DesignSystem.kt
                    object AppDesignSystem {
                        val colors = AppColors
                        val typography = AppTypography
                        val spacing = AppSpacing
                    }

                    @Composable
                    fun AppTheme(content: @Composable () -> Unit) {
                        MaterialTheme(
                            colorScheme = AppDesignSystem.colors,
                            typography = AppDesignSystem.typography,
                            content = content
                        )
                    }

                    // Shared Composables
                    @Composable
                    fun AppButton(
                        text: String,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Button(onClick = onClick, modifier = modifier) {
                            Text(text)
                        }
                    }

                    // 2️⃣ :feature:products:domain - Business Logic
                    // ═══════════════════════════════════════════════════════════════
                    // NO Compose dependencies!
                    class GetProductsUseCase @Inject constructor(
                        private val repository: ProductRepository
                    ) {
                        operator fun invoke(): Flow<Result<List<Product>>> {
                            return repository.getProducts()
                        }
                    }

                    // 3️⃣ :feature:products:ui - Compose UI Layer
                    // ═══════════════════════════════════════════════════════════════
                    // build.gradle.kts
                    dependencies {
                        implementation(project(":feature:products:domain"))
                        implementation(project(":core:ui"))
                        implementation(libs.hilt.navigation.compose)
                    }

                    // Navigation
                    fun NavGraphBuilder.productsNavGraph(navController: NavController) {
                        navigation(
                            startDestination = "products/list",
                            route = "products"
                        ) {
                            composable("products/list") {
                                ProductListScreen(
                                    onNavigateToDetail = { id ->
                                        navController.navigate("products/detail/$id")
                                    }
                                )
                            }
                            
                            composable(
                                route = "products/detail/{productId}",
                                arguments = listOf(navArgument("productId") { type = NavType.StringType })
                            ) {
                                val productId = it.arguments?.getString("productId")!!
                                ProductDetailScreen(productId = productId)
                            }
                        }
                    }

                    // Screen
                    @Composable
                    fun ProductListScreen(
                        viewModel: ProductListViewModel = hiltViewModel(),
                        onNavigateToDetail: (String) -> Unit
                    ) {
                        val state by viewModel.state.collectAsState()
                        
                        AppTheme {
                            LazyColumn {
                                items(state.products, key = { it.id }) { product ->
                                    ProductCard(
                                        product = product,
                                        onClick = { onNavigateToDetail(product.id) }
                                    )
                                }
                            }
                        }
                    }

                    // 4️⃣ :app Module - Composition Root
                    // ═══════════════════════════════════════════════════════════════
                    @AndroidEntryPoint
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            
                            setContent {
                                AppTheme {
                                    val navController = rememberNavController()
                                    
                                    NavHost(
                                        navController = navController,
                                        startDestination = "products"
                                    ) {
                                        productsNavGraph(navController)
                                        cartNavGraph(navController)
                                        profileNavGraph(navController)
                                    }
                                }
                            }
                        }
                    }

                    // 5️⃣ Module Communication
                    // ═══════════════════════════════════════════════════════════════
                    // Feature modules DON'T depend on each other
                    // Use shared :core:navigation module for navigation contracts

                    // :core:navigation
                    sealed class NavigationDestination(val route: String) {
                        object ProductList : NavigationDestination("products/list")
                        data class ProductDetail(val id: String) : NavigationDestination("products/detail/$id")
                        object Cart : NavigationDestination("cart")
                    }

                    // Navigator interface
                    interface AppNavigator {
                        fun navigate(destination: NavigationDestination)
                        fun navigateBack()
                    }

                    // Implementation in :app
                    class AppNavigatorImpl(
                        private val navController: NavController
                    ) : AppNavigator {
                        override fun navigate(destination: NavigationDestination) {
                            navController.navigate(destination.route)
                        }
                        
                        override fun navigateBack() {
                            navController.popBackStack()
                        }
                    }

                    // Inject in feature modules
                    @HiltViewModel
                    class ProductViewModel @Inject constructor(
                        private val navigator: AppNavigator
                    ) : ViewModel() {
                        fun onProductClick(id: String) {
                            navigator.navigate(NavigationDestination.ProductDetail(id))
                        }
                    }

                    // 6️⃣ Testing Strategy
                    // ═══════════════════════════════════════════════════════════════
                    // Each module has test dependencies:

                    // :core:ui:test - Test utilities
                    object ComposeTestUtils {
                        fun createComposeRule() = createComposeRule()
                        
                        fun setTheme(content: @Composable () -> Unit) = AppTheme {
                            content()
                        }
                    }

                    // Feature test (:feature:products:ui:test)
                    class ProductScreenTest {
                        @get:Rule
                        val composeTestRule = createComposeRule()
                        
                        @Test
                        fun `displays products`() {
                            composeTestRule.setContent {
                                ComposeTestUtils.setTheme {
                                    ProductListScreen(
                                        viewModel = FakeProductViewModel(),
                                        onNavigateToDetail = {}
                                    )
                                }
                            }
                        }
                    }

                    // ═══════════════════════════════════════════════════════════════
                    // BENEFITS:
                    // ✅ Clear separation of concerns
                    // ✅ Parallel development (teams work independently)
                    // ✅ Faster build times (Gradle caching)
                    // ✅ Reusable components
                    // ✅ Testable (mock dependencies)
                    // ✅ Scalable (add features without affecting others)
                    // ═══════════════════════════════════════════════════════════════
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //MVI Validation 
                    
                    class EvaluatePasswordPolicyUseCase {
                        operator fun invoke(password: String): List<PasswordRule> {
                            return listOf(
                                PasswordRule(PasswordRuleId.MIN_LEN_8, password.length >= 8),
                                PasswordRule(PasswordRuleId.HAS_UPPER, password.any { it.isUpperCase() }),
                                 PasswordRule(PasswordRuleId.HAS_LOWER, password.any { it.isLowerCase() }),
                                PasswordRule(PasswordRuleId.HAS_SYMBOL, password.any { !it.isLetterOrDigit() })
                            )
                        }
                    }
                    
                    class RegisterViewModel : ViewModel() {

                        private val _uiState = MutableStateFlow(RegisterUiState())
                        val uiState: StateFlow<RegisterUiState> = _uiState

                        fun onEvent(event: RegisterUiEvent) {
                            when (event) {

                                is RegisterUiEvent.EmailChanged -> {
                                    _uiState.update { current ->
                                        current.copy(
                                            email = event.value
                                        )
                                    }
                                }

                                is RegisterUiEvent.PasswordChanged -> {
                                    _uiState.update { current ->

                                        val newPassword = event.value

                                        val rulesDomain = evaluatePasswordPolicy(newPassword)
                                        val rulesUi = rulesDomain.map(PasswordRuleUiMapper::map)

                                        val isPasswordValid = rulesDomain.all { it.satisfied }

                                        current.copy(
                                            password = newPassword,
                                            passwordError = null,
                                            passwordRuleState = rulesUi,
                                            confirmPasswordError =
                                                if (current.confirmPassword.isNotBlank() &&
                                                    current.confirmPassword != newPassword) 
                                                        "Passwords do not match"
                                                else null,
                                            canSubmit = isPasswordValid
                                        )
                                    }
                                }

                                is RegisterUiEvent.ConfirmPasswordChanged -> {
                                    _uiState.update { current ->
                                        current.copy(
                                            confirmPassword = event.value,
                                            confirmPasswordError =
                                                if (event.value != current.password)
                                                    "Passwords do not match"
                                                else null
                                        )
                                    }
                                }

                                RegisterUiEvent.Submit -> {
                                    submit()
                                }
                            }
                        }

                        private fun submit() {
                            println("Submit registration")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Deeply Clean Code",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    interface FeatureFlagRepository {
                        suspend fun isEnabled(flag: FeatureFlag): Boolean
                    }
                    
                    enum class FeatureFlag(val key: String) {
                        NEW_CHECKOUT_FLOW("new_checkout_flow"),
                        DARK_MODE_V2("dark_mode_v2")
                    }
                    
                    class FirebaseFeatureFlagRepository(
                        private val remoteConfig: FirebaseRemoteConfig
                    ) : FeatureFlagRepository {

                        override suspend fun isEnabled(flag: FeatureFlag): Boolean {
                            return remoteConfig.getBoolean(flag.key)
                        }
                    }
                    
                    class GetCheckoutFlowUseCase(
                        private val flags: FeatureFlagRepository
                    ) {

                        suspend operator fun invoke(): CheckoutFlow {
                            val enabled = flags.isEnabled(FeatureFlag.NEW_CHECKOUT_FLOW)

                            return if (enabled) {
                                CheckoutFlow.Redesign
                            } else {
                                CheckoutFlow.Legacy
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class Order(
                        val id: String,
                        val title: String,
                        val updatedAt: Instant,
                        val isDirty: Boolean // locally modified but not synced
                    )
                    
                    data class SyncStatus(
                        val isSyncing: Boolean = false,
                        val lastSyncedAt: Instant? = null,
                        val pendingChanges: Int = 0
                    )
                    
                    data class OrderListUiState(
                        val orders: List<Order> = emptyList(),
                        val syncStatus: SyncStatus = SyncStatus(),
                        val error: UiError? = null
                    )
                    
                    class FakeOrderRepository {

                        private val orders = MutableStateFlow<List<Order>>(emptyList())

                        fun observeOrders(): Flow<List<Order>> = orders

                        suspend fun addOrder(title: String) {
                            val new = Order(
                                id = UUID.randomUUID().toString(),
                                title = title,
                                isDirty = true,
                                updatedAt = Clock.System.now()
                            )

                            orders.update { it + new }
                        }

                        suspend fun updateOrder(id: String, title: String) {

                            orders.update { list ->
                                list.map {
                                    if (it.id == id)
                                        it.copy(title = title, isDirty = true)
                                    else it
                                }
                            }
                        }

                        suspend fun sync(): SyncStatus {

                            delay(2000) // simulate network

                            orders.update { list ->
                                list.map { it.copy(isDirty = false) }
                            }

                            val updated = orders.value

                            return SyncStatus(
                                isSyncing = false,
                                lastSyncedAt = Clock.System.now(),
                                pendingChanges = updated.count { it.isDirty }
                            )
                        }
                    }
                    
                    sealed interface OrderIntent {
                        data object Load : OrderIntent

                        data object Sync : OrderIntent

                        data class AddOrder(val title: String) : OrderIntent

                        data class UpdateOrder(
                            val id: String,
                            val title: String
                        ) : OrderIntent
                    }
                    
                    //ViewModel
                    fun onIntent(intent: OrderIntent) {
                        when (intent) {
                            OrderIntent.Load -> {}

                            OrderIntent.Sync -> sync()

                            is OrderIntent.AddOrder ->
                                addOrder(intent.title)

                            is OrderIntent.UpdateOrder ->
                                updateOrder(intent.id, intent.title)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    sealed interface PaymentEffect {
                        data class ShowError(val message: String) : PaymentEffect
                        data object NavigateToReceipt : PaymentEffect
                    }
                    
                    data class PaymentState(
                        val isLoading: Boolean = false,
                        val result: PaymentResult? = null,
                        val error: String? = null
                    )
                    
                    sealed interface PaymentIntent {
                        data class ProcessPayment(val amount: Long) : PaymentIntent
                    }
                    
                    class PaymentViewModel(
                        private val paymentUseCase: ProcessPaymentUseCase
                    ) : ViewModel() {

                        // STATE
                        private val _state = MutableStateFlow(PaymentState())
                        val state = _state.asStateFlow()

                        // EFFECT (one-time)
                        private val _effect = Channel<PaymentEffect>()
                        val effect = _effect.receiveAsFlow()

                        // INTENT ENTRY POINT
                        fun onIntent(intent: PaymentIntent) {
                            when (intent) {
                                is PaymentIntent.ProcessPayment -> processPayment(intent.amount)
                            }
                        }

                        private fun processPayment(amount: Long) {
                            viewModelScope.launch {
                                _state.update { it.copy(isLoading = true, error = null) }

                                try {
                                    paymentUseCase(amount)
                                        .onSuccess { result ->
                                            _state.update {
                                                it.copy(
                                                    isLoading = false,
                                                    result = result
                                                )
                                            }
                                            _effect.send(PaymentEffect.NavigateToReceipt)
                                        }
                                        .onFailure { error ->
                                            val message = error.message ?: "Something went wrong"

                                            _state.update {
                                                it.copy(
                                                    isLoading = false,
                                                    error = message
                                                )
                                            }

                                            _effect.send(PaymentEffect.ShowError(message))
                                        }

                                } catch (e: Exception) {
                                    val message = e.message ?: "Unexpected error"

                                    _state.update {
                                        it.copy(
                                            isLoading = false,
                                            error = message
                                        )
                                    }

                                    _effect.send(PaymentEffect.ShowError(message))
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/synapseSRC/synapseApp
            KotlinCode(
                code = """
                    import kotlinx.coroutines.channels.BufferOverflow
                    import kotlinx.coroutines.flow.MutableSharedFlow
                    import kotlinx.coroutines.flow.SharedFlow
                    import kotlinx.coroutines.flow.asSharedFlow

                    sealed class UiEvent {
                        data class Message(val text: String) : UiEvent()
                        data class Error(val text: String) : UiEvent()
                        data class Success(val text: String) : UiEvent()
                    }

                    object UiEventManager {
                        private val _events = MutableSharedFlow<UiEvent>(
                            replay = 1,
                            onBufferOverflow = BufferOverflow.DROP_OLDEST
                        )
                        val events: SharedFlow<UiEvent> = _events.asSharedFlow()

                        suspend fun emit(event: UiEvent) {
                            _events.emit(event)
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/AliBehrouzinia/Task-Manager
            KotlinCode(
                code = """
                    data class TaskUiState(
                        val title: String, val id: Int, val isDone: Boolean
                    )
                    
                    sealed interface HomeStatus {
                        object Loading : HomeStatus
                        object Content : HomeStatus
                        object Empty : HomeStatus
                        data class Error(val error: ErrorResult) : HomeStatus
                    }
                    
                    data class HomeUiState(
                        val tasks: List<TaskUiState> = emptyList(),
                        val status: HomeStatus = HomeStatus.Loading
                    )

                    fun HomeUiState.asLoading(): HomeUiState =
                        copy(status = HomeStatus.Loading)
                    
                    fun HomeUiState.asContent(tasks: List<TaskUiState>): HomeUiState =
                        copy(
                            tasks = tasks,
                            status = if (tasks.isEmpty()) HomeStatus.Empty else HomeStatus.Content
                        )
                    
                    fun HomeUiState.asError(error: ErrorResult): HomeUiState =
                        copy(status = HomeStatus.Error(error))
                    
                    fun HomeUiState.updateTask(updatedTask: TaskUiState): HomeUiState =
                        copy(
                            tasks = tasks.map { task ->
                                if (task.id == updatedTask.id) updatedTask else task
                            },
                            status = HomeStatus.Content
                        )
                    
                     @HiltViewModel
                     class HomeViewModel @Inject constructor(
                         val getTasksUseCase: GetTasksUseCase, val updateTaskUseCase: UpdateTaskUseCase
                     ) : ViewModel() {
                         private val _uiState = MutableStateFlow(HomeUiState())
                         val uiState: StateFlow<HomeUiState> = _uiState.asStateFlow()

                         init {
                             viewModelScope.launch {
                                 getTasksUseCase.tasks.collect { result ->
                                     when (result) {
                                         is Result.Error -> _uiState.update { it.asError(result.error) }
                                         is Result.Data -> _uiState.update {
                                             it.asContent(result.data.map { task ->
                                                 task.toUiTaskState()
                                             })
                                         }
                                     }
                                 }
                             }
                         }

                         fun onTaskClick(task: TaskUiState) {
                             viewModelScope.launch {
                                 updateTaskUseCase(task = task.copy(isDone = !task.isDone).toDomain()).collect { result ->
                                     when (result) {
                                         is Result.Error -> _uiState.update { it.asError(result.error) }
                                         is Result.Data -> _uiState.update { uiState ->
                                             uiState.asContent(uiState.tasks.map {
                                                 if (it.id == task.id) {
                                                     result.data.toUiTaskState()
                                                 } else {
                                                     it
                                                 }
                                             })
                                         }
                                     }
                                 }
                             }
                         }
                     }
                    """.trimIndent()
            )

            SpanText(
                text = "Adapting MVI for iOS",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In our case with MVI on KMP, we still had to adapt the native UI code. We used four layers of abstraction:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In commonMain source set of MVI Flow we had CommonViewModel with a suspend function to receive Intents from UI and a Coroutines Flow for State updates. It was used in both Android & iOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In iosMain, the Coroutines Flow was wrapped with CFlow, and iOSViewModel extended CommonViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In native iOS, it was wrapped into @Published + ViewModel: ObservableObject, enabling its use in SwiftUI. However, this was not our case, as iOS engineers were uncertain about using SwiftUI yet.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We adopted this ViewModel for UIKit in presenters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is the difference between MVVM and MVI, and when would you choose one over the other?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVVM uses two-way data binding with a ViewModel exposing LiveData/StateFlow.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVI enforces a strict unidirectional data flow — Intent → Model → View — making state changes fully predictable and testable. MVI shines in complex UIs with many user interactions (e.g. a real-time map screen), where shared mutable state in MVVM can cause bugs.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How MVI Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "User Interaction: the user interacts with the UI (e.g., clicks a button), triggering an Intent.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Intent Handling: the View sends the Intent to the ViewModel.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Business Logic: the ViewModel processes the Intent, interacts with the data layer (if needed), and computes the new Model (state).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Update: the ViewModel emits the updated state back to the View.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI Update: the View observes the state changes and updates the UI accordingly.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can you explain the Repository pattern and how it helps with code organization and separation of concerns?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Repository Pattern is a design pattern used to abstract and centralize data access logic, which helps with code organization and separation of concerns. It serves as a bridge between the Domain Layer (business logic) and the Data Layer (data sources such as local databases, remote APIs, and cache). This allows the application’s core logic to remain independent of specific data source implementations, making the codebase easier to maintain, test, and scale.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Repository Pattern abstracts data access, hiding the details of data sources from the rest of the app.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Separates concerns by isolating data handling logic, keeping the UI and Domain layers focused on their specific roles.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Improves testability by allowing you to mock the Repository for unit tests.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Centralizes data management and caching, making it easier to implement and maintain complex data operations.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MVVM Vs MVI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "MVVM",
                    modifier = Modifier.fontSize(FontSize.Large).padding(bottom = 1.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "View observes state",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel exposes action methods",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State is updated imperatively",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Two-way communication (View « ViewModel)",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "MVI",
                    modifier = Modifier.fontSize(FontSize.Large).padding(bottom = 1.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Strict one-way flow: Intent → Processor → Reducer → State → View",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Actions become immutable Intents",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Reducer is a pure function: (currentState + Intent) → newState",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Middleware/Processor handles all side effects",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}

//https://github.com/alexiyous/ramadhan-camp-kmp.git (Common ViewModel With Parameter Pass, AnimatedContent using API Calling Handle (Error, Loading, Success) MVI, API Calling Good)
//https://github.com/palak3005/MVI-Counter-App
//https://github.com/ImChabs/DontForgetApp.git (Rood DB MVI)
//https://github.com/natobytes/KMVI
//https://medium.com/@connectwitharunrk/mvi-in-jetpack-compose-the-definitive-guide-b348dae98cb5
//https://github.com/RamadanElSayed/MovieApp.git (common viewmodel)
//https://github.com/NsAveek/InventoryManagement.git (common viewmodel, object intent)
//https://abhinay212.medium.com
//https://github.com/kosi-libs/VIE
//https://medium.com/@mjaahamed/managing-data-and-state-in-modern-mobile-apps-mvc-mvp-mvvm-and-mvi-compared-0ceddbe83a6b